/**
 * Which shell answers a URL, after the cutover.
 *
 * `/` is the reimagined app. The legacy shell is NOT deleted — the owner asked
 * for it kept as a fallback — it moves wholesale to `/legacy`, mounted in its
 * own router with `basename="/legacy"`. That basename is the reason App.tsx
 * needs no edit at all: its thirty absolute routes and every `<NavLink to="/…">`
 * inside it keep working verbatim, and only the address bar changes.
 *
 * Two routers rather than one, because a basename is a property of the router,
 * not of a route, and the two shells now need different ones.
 *
 * This is a pure function so the redirect table can be tested without a DOM. It
 * is the one piece of the cutover that silently sends people to the wrong place
 * when it is wrong.
 */

export type ShellChoice =
  | { shell: 'new' }
  | { shell: 'legacy' }
  /** Hard redirect (the two shells are separate router roots — a push would
   *  render one shell against the other's path). */
  | { shell: 'redirect'; to: string };

export const LEGACY_BASE = '/legacy';

/**
 * Every path the legacy shell answered before the cutover, enumerated rather
 * than matched by prefix.
 *
 * A prefix rule would be shorter and would be wrong: `/schedule` is a JOURNEY
 * in the new shell (the activity grid) and a MODULE ROOT in the legacy one, so
 * `startsWith('/schedule')` hands the new app's own screen to a redirect. The
 * legacy shell is frozen, so an explicit list cannot drift out of date — which
 * is exactly the condition that makes enumeration the safer choice here.
 */
export const LEGACY_PATHS: readonly string[] = [
  '/open',
  '/schedule/overview', '/schedule/project', '/schedule/tasks', '/schedule/trends',
  '/schedule/compliance', '/schedule/reports', '/schedule/projects',
  '/delay/chain', '/delay/window', '/delay/tia',
  '/contract/entitlement',
  '/claim/report', '/claim/timeline', '/claim/admit', '/claim/exhibits',
  '/claim/notice-compliance', '/claim/connectors',
  // The legacy shell's own aliases for pre-2026 bookmarks. Kept here so a link
  // that was already two redirects old still lands somewhere real.
  '/dashboard', '/project', '/trends', '/reports', '/compliance', '/delays',
];

/** Journeys the new shell owns. Single-segment, all of them. */
const NEW_JOURNEYS = new Set([
  'overview', 'health', 'schedule', 'findings', 'forensics', 'timeimpact',
  'risk', 'evidence', 'claim', 'projects', 'gates',
  // Operator console (B1b) — palette-only, but a bookmark to it must resolve here.
  'admin',
  // B4 signup + its Stripe return URL. Public, pre-account, and rendered
  // WITHOUT the shell — but still this router's to answer.
  'signup',
]);

function normalize(pathname: string): string {
  // Trailing slashes are the difference between a bookmark that works and one
  // that does not, and nobody types them deliberately.
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed || '/';
}

export function resolveShell(pathname: string): ShellChoice {
  const path = normalize(pathname);

  if (path === LEGACY_BASE || path.startsWith(`${LEGACY_BASE}/`)) return { shell: 'legacy' };

  // The preview mount point. Anyone who bookmarked a journey during the parallel
  // run keeps it: /newapp/forensics -> /forensics.
  if (path === '/newapp') return { shell: 'redirect', to: '/' };
  if (path.startsWith('/newapp/')) {
    const rest = path.slice('/newapp'.length);
    return { shell: 'redirect', to: rest || '/' };
  }

  // A legacy bookmark. `/schedule` is deliberately absent from LEGACY_PATHS —
  // it is the new shell's activity grid, and the legacy module root only ever
  // redirected to /schedule/overview anyway.
  if (LEGACY_PATHS.includes(path)) return { shell: 'redirect', to: `${LEGACY_BASE}${path}` };

  // Bare legacy module roots, which used to bounce to their first subpage.
  // `/schedule` is excluded for the reason above; `/claim` likewise belongs to
  // the new shell now (the claim studio).
  if (path === '/delay' || path === '/contract') {
    return { shell: 'redirect', to: `${LEGACY_BASE}${path}` };
  }

  if (path === '/') return { shell: 'new' };
  const first = path.slice(1).split('/')[0]!;
  if (NEW_JOURNEYS.has(first)) return { shell: 'new' };

  // Unknown. The new shell owns the root now, so an unrecognised path is its
  // 404 to answer, not a reason to guess at the legacy app.
  return { shell: 'new' };
}
